import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { typographies } from "../../../styles/figmaTypographies";
import { Spacer } from "../Spacer";
import {
  Trip,
  PlanningInfo,
  TransactionAndAuthorizationOnTrip,
} from "./DayTripDetails";
import { TripOrPlanningDetailsVertical } from "./TripOrPlanningDetailsVertical";
import { colors } from "../../../styles/figmaColors";
import { LittleCardAsset } from "../../../assets/assets";

const minutesSize = 0.05;
const hoursColumnWidth = 3;
const detailsCardOffsetY = 14;

export interface HoursBarTripsOrPlanningVerticalProps {
  trips: Trip[];
  driverName: string | null;
  plannings: PlanningInfo[];
  transactionsToDisplay: TransactionAndAuthorizationOnTrip[];
}

const getHoursAndMinutesFromMinutes = (minutes: number): string => {
  const hours = minutes > 59 ? Math.floor(minutes / 60) : 0;
  const minutesLeft = hours ? minutes - hours * 60 : minutes;
  return `${hours}:${minutesLeft < 10 ? "0" + minutesLeft : minutesLeft}`;
};

const isOnTwoDays = (startDate: string, endDate: string): boolean => {
  return new Date(startDate).getDay() != new Date(endDate).getDay();
};

const getDurationInMinutes = (
  startDate: string,
  endDate: string,
  isOnTwoDays: boolean,
): number => {
  return (
    (new Date(endDate).getHours() - new Date(startDate).getHours()) * 60 +
    new Date(endDate).getMinutes() -
    new Date(startDate).getMinutes() +
    (isOnTwoDays ? 24 * 60 : 0)
  );
};

export const HoursBarTripsOrPlanningVertical = (
  props: HoursBarTripsOrPlanningVerticalProps,
) => {
  const [tripOverview, setTripOverview] = useState<number | null>(null);
  const [planningOverview, setPlanningOverview] = useState<number | null>(null);
  const [mouseYPosition, setMouseYPosition] = useState<number>(0);
  const [currentTimeOverview, setCurrentTimeOverview] = useState<number | null>(
    null,
  );
  const hoursBarContainerRef = useRef<HTMLDivElement>(null);

  const scrollToFirstTrip = useCallback(
    (barRef: React.RefObject<HTMLDivElement>) => {
      const container = hoursBarContainerRef.current;
      const bar = barRef.current;
      if (!container || !bar) return;

      // Avoid scrollIntoView: it can scroll ancestor overflow areas (e.g. PageDS), not only this container.
      const containerRect = container.getBoundingClientRect();
      const barRect = bar.getBoundingClientRect();
      // Viewport distance from the bar's top to the container's visible top (where the scrollport starts on screen).
      const offsetBarTopInsideVisibleContainer =
        barRect.top - containerRect.top;
      // Target scrollTop: align the bar's top with the container's visible top (scroll up as far as this bar allows).
      const nextTop = container.scrollTop + offsetBarTopInsideVisibleContainer;
      // Maximum scrollTop for this container (content height minus visible height); never negative.
      const maxTop = Math.max(
        0,
        container.scrollHeight - container.clientHeight,
      );
      // Clamp: do not scroll above the start of content or past the end of content.
      container.scrollTo({
        top: Math.max(0, Math.min(nextTop, maxTop)),
        behavior: "smooth",
      });
    },
    [],
  );

  //DATA FOR DISPLAYING THE TRIP DETAILS
  const currentTrip = tripOverview !== null ? props.trips[tripOverview] : null;
  const isCurrentTripOnTwoDays = !!(
    currentTrip && isOnTwoDays(currentTrip.startDate, currentTrip.endDate)
  );

  const currentTripDurationInMinutes = currentTrip
    ? getDurationInMinutes(
        currentTrip.startDate,
        currentTrip.endDate,
        isCurrentTripOnTwoDays,
      )
    : 0;

  const tripData = currentTrip && {
    ...currentTrip,
    driver: props.driverName,
    duration: Number(currentTripDurationInMinutes.toFixed(0)),
  };

  //DATA FOR DISPLAYING THE PLANNING DETAILS
  const currentPlanning =
    planningOverview !== null ? props.plannings[planningOverview] : null;
  const isCurrentPlanningOnTwoDays = !!(
    currentPlanning &&
    isOnTwoDays(currentPlanning.startTime, currentPlanning.endTime)
  );

  const currentDurationInMinutes = currentPlanning
    ? getDurationInMinutes(
        currentPlanning.startTime,
        currentPlanning.endTime,
        isCurrentPlanningOnTwoDays,
      )
    : 0;
  const planningData = currentPlanning && {
    ...currentPlanning,
    driver: props.driverName,
    duration: Number(currentDurationInMinutes.toFixed(0)),
  };

  //HANDLE IF THE LAST DATE ENDS ON ANOTHER DAY
  const lastTrip = props.trips[props.trips.length - 1];
  const lastPlanning = props.plannings[props.plannings.length - 1];

  const isLastTripOnAnotherDay =
    lastTrip && isOnTwoDays(lastTrip.startDate, lastTrip.endDate);

  const isLastPlanningOnAnotherDay =
    lastPlanning && isOnTwoDays(lastPlanning.startTime, lastPlanning.endTime);

  const lastEndDate =
    isLastTripOnAnotherDay || isLastPlanningOnAnotherDay
      ? !lastPlanning?.endTime || lastTrip.endDate > lastPlanning?.endTime
        ? lastTrip.endDate
        : lastPlanning.endTime
      : null;

  const lastEndDateInMinutes =
    lastEndDate &&
    new Date(lastEndDate).getHours() * 60 +
      new Date(lastEndDate).getMinutes() +
      10;

  //GET MINUTES TO DISPLAYED
  const minutesInADay = Array.from({ length: 24 * 60 }, (_, m) => m);
  const minutesExceededOnAnotherDay = lastEndDateInMinutes
    ? Array.from({ length: 24 * 60 }, (_, m) => {
        if (m < lastEndDateInMinutes) {
          return m + 24 * 60;
        }
        return undefined;
      }).filter((m) => m !== undefined)
    : [];

  const minutesToDisplayIndex = [
    ...minutesInADay,
    ...minutesExceededOnAnotherDay,
  ];

  //GET HOURS TO DISPLAY
  const hoursInADay = Array.from({ length: 24 }, (_, h) => h);

  const isOnOtherDay = isLastTripOnAnotherDay || isLastPlanningOnAnotherDay;
  const hoursExceededOnAnotherDay =
    isOnOtherDay && lastEndDateInMinutes
      ? Array.from(
          { length: Math.ceil(lastEndDateInMinutes / 60) + 1 },
          (_, h) => h + 24,
        )
      : [];

  const hoursToDisplay = [...hoursInADay, ...hoursExceededOnAnotherDay];

  return (
    <StyledHoursBarContainer className="hours-bar-container">
      <StyledHoursBarContainerOverflow ref={hoursBarContainerRef}>
        {tripData && (
          <StyledFlex>
            <TripOrPlanningDetailsVertical
              dataDisplay={tripData}
              type={"TRIP"}
              topPosition={mouseYPosition - detailsCardOffsetY}
            />
          </StyledFlex>
        )}
        {planningData && (
          <StyledFlex>
            <TripOrPlanningDetailsVertical
              type={"PLANNING"}
              topPosition={mouseYPosition - detailsCardOffsetY}
              dataDisplay={{
                driver: planningData.driver,
                duration: planningData.duration,
                startDate: planningData.startTime,
                endDate: planningData.endTime,
                distance: Number(planningData.expectedMileage.toFixed(2)),
                clientName: planningData.clientName,
                clientCity: planningData.clientCity,
                hasKmClient: planningData.hasKmClient,
                activityType: planningData.activityType,
                isOutIntervention: planningData.isOutIntervention,
              }}
            />
          </StyledFlex>
        )}
        <StyledHoursBarDetails>
          <Spacer x={3.5} />
          <StyledVerticalBar $hours={hoursToDisplay.length} />
          <StyledMinutesTrack>
            {minutesToDisplayIndex.map((index) => {
              const minutes = index < 24 * 60 ? index : index - 24 * 60;
              return (
                <StyledMinuteSlot
                  key={index}
                  onMouseEnter={() => {
                    setCurrentTimeOverview(minutes);
                  }}
                  onMouseLeave={() => {
                    setCurrentTimeOverview(null);
                  }}
                >
                  {currentTimeOverview && currentTimeOverview === minutes && (
                    <>
                      <StyledHoverMinuteIndicator>
                        <StyledLineGreen />
                        <Spacer y={0.25} />
                        <StyledHourOverview>
                          <Spacer y={0.25} />
                          <StyledFlex>
                            <Spacer x={3} />
                            {getHoursAndMinutesFromMinutes(currentTimeOverview)}
                            <Spacer x={3} />
                          </StyledFlex>
                          <Spacer y={0.25} />
                        </StyledHourOverview>
                      </StyledHoverMinuteIndicator>
                    </>
                  )}
                </StyledMinuteSlot>
              );
            })}
          </StyledMinutesTrack>
          <StyledAbsoluteHoursBar>
            {hoursToDisplay.map((hour) => (
              <StyledHour key={hour}>
                <StyledHourLabel>{(hour % 24) + " H"}</StyledHourLabel>
                <StyledHourLine />
              </StyledHour>
            ))}
          </StyledAbsoluteHoursBar>
          <StyledFlexTripsAndTransactions>
            <TripsOnHoursBarVertical
              trips={props.trips}
              transactionsToDisplay={props.transactionsToDisplay}
              setTripOverview={setTripOverview}
              setMouseYPosition={setMouseYPosition}
              containerRef={hoursBarContainerRef}
              scrollToFirstTrip={scrollToFirstTrip}
            />
            {props.transactionsToDisplay.length > 0 && (
              <StyledAbsoluteTransaction>
                <TransactionsOnHoursBar
                  transactionsToDisplay={props.transactionsToDisplay}
                />
              </StyledAbsoluteTransaction>
            )}
          </StyledFlexTripsAndTransactions>
          <Spacer x={0.5} />
          {props.plannings.length > 0 && (
            <>
              <PlanningsOnHoursBarVertical
                plannings={props.plannings}
                setPlanningOverview={setPlanningOverview}
                setMouseYPosition={setMouseYPosition}
                containerRef={hoursBarContainerRef}
              />
              {props.plannings.length > 0 && <Spacer x={0.5} />}
            </>
          )}
        </StyledHoursBarDetails>
      </StyledHoursBarContainerOverflow>
    </StyledHoursBarContainer>
  );
};

const TripsOnHoursBarVertical = (props: {
  trips: Trip[];
  setTripOverview: (index: number | null) => void;
  setMouseYPosition: (y: number) => void;
  containerRef: React.RefObject<HTMLDivElement>;
  transactionsToDisplay: TransactionAndAuthorizationOnTrip[];
  scrollToFirstTrip: (barRef: React.RefObject<HTMLDivElement>) => void;
}) => {
  const { trips, scrollToFirstTrip } = props;
  const firstTripRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (firstTripRef.current) {
      scrollToFirstTrip(firstTripRef);
    }
  }, [scrollToFirstTrip, trips]);

  return (
    <StyledDisplayOnHoursBar>
      <Spacer y={1.5} />
      {props.trips.map((trip, index) => {
        const isTripsOnTwoDays = isOnTwoDays(trip.startDate, trip.endDate);
        const durationInMinutes = getDurationInMinutes(
          trip.startDate,
          trip.endDate,
          isTripsOnTwoDays,
        );

        const previousTrip = props.trips[index - 1];
        const gapTimeWithPreviousTripInMinutes = previousTrip
          ? (new Date(trip.startDate).getTime() -
              new Date(previousTrip.endDate).getTime()) /
            60000
          : new Date(trip.startDate).getHours() * 60 +
            new Date(trip.startDate).getMinutes();

        return (
          <StyledFlex key={"trip" + index}>
            <Spacer y={gapTimeWithPreviousTripInMinutes * minutesSize} />
            <StyledPeriod
              ref={index === 0 ? firstTripRef : undefined}
              $height={durationInMinutes * minutesSize}
              onMouseEnter={(e) => {
                props.setMouseYPosition(e.clientY / 16);
                props.setTripOverview(index);
              }}
              onMouseLeave={() => props.setTripOverview(null)}
              $color={"#00FFA3"}
            />
          </StyledFlex>
        );
      })}
    </StyledDisplayOnHoursBar>
  );
};

const TransactionsOnHoursBar = (props: {
  transactionsToDisplay: TransactionAndAuthorizationOnTrip[];
}) => {
  return (
    <StyledDisplayOnHoursBar>
      <Spacer y={1.5} />
      {props.transactionsToDisplay.map((t, index) => {
        const cardHeightInMinutes = 8.5;
        const previousTransaction = props.transactionsToDisplay[index - 1];

        const gapTimeWithPreviousTransactionInMinutes = previousTransaction
          ? (new Date(t.date).getTime() -
              new Date(previousTransaction.date).getTime()) /
              60000 -
            cardHeightInMinutes
          : new Date(t.date).getHours() * 60 + new Date(t.date).getMinutes();

        return (
          <>
            <StyledFlex>
              <Spacer
                y={gapTimeWithPreviousTransactionInMinutes * minutesSize}
              />
              <StyledFlexHorizontal key={"transaction" + index}>
                <LittleCardAsset />
              </StyledFlexHorizontal>
            </StyledFlex>
          </>
        );
      })}
    </StyledDisplayOnHoursBar>
  );
};

const PlanningsOnHoursBarVertical = (props: {
  plannings: PlanningInfo[];
  setPlanningOverview: (index: number | null) => void;
  setMouseYPosition: (y: number) => void;
  containerRef: React.RefObject<HTMLDivElement>;
}) => {
  return (
    <StyledDisplayOnHoursBar>
      <Spacer y={1.5} />
      {props.plannings.map((p, index) => {
        const isPlanningOnTwoDays = isOnTwoDays(p.startTime, p.endTime);
        const durationInMinutes = getDurationInMinutes(
          p.startTime,
          p.endTime,
          isPlanningOnTwoDays,
        );

        const previousPlanning = props.plannings[index - 1];
        const gapTimeWithPreviousTripInMinutes = previousPlanning
          ? (new Date(p.startTime).getTime() -
              new Date(previousPlanning.endTime).getTime()) /
            60000
          : new Date(p.startTime).getHours() * 60 +
            new Date(p.startTime).getMinutes();

        return (
          <StyledFlex key={"planning" + index}>
            <Spacer y={gapTimeWithPreviousTripInMinutes * minutesSize} />
            <StyledPeriod
              $height={durationInMinutes * minutesSize}
              onMouseEnter={(e) => {
                props.setMouseYPosition(e.clientY / 16);
                props.setPlanningOverview(index);
              }}
              onMouseLeave={() => props.setPlanningOverview(null)}
              $color={
                p.isOutIntervention
                  ? colors["colors/system/informative/informative_light"]
                  : "#D1E9FF"
              }
            />
          </StyledFlex>
        );
      })}
    </StyledDisplayOnHoursBar>
  );
};

const StyledMinutesTrack = styled.div`
  position: absolute;
  z-index: 10;
  top: 1.5rem;
`;

const StyledMinuteSlot = styled.div`
  position: relative;
  flex: 2;
  min-height: ${minutesSize}rem;
  max-height: ${minutesSize}rem;
  width: ${hoursColumnWidth}rem;
`;

const StyledHoverMinuteIndicator = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 30rem;
  pointer-events: none;
`;

const StyledHourOverview = styled.div`
  background-color: ${"#00FFA3"};
  color: ${colors["colors/text/darkGrey"]};
  ${typographies["Body/XXS"]};
  border-radius: 1rem;
  top: -1.75rem;
  position: absolute;
`;

const StyledLineGreen = styled.div`
  width: 100%;
  border-top: 0.2rem solid #00ffa3;
`;

const StyledAbsoluteHoursBar = styled.div`
  position: absolute;
  color: ${colors["colors/text/lightGrey"]};
  ${typographies["Body/XXS"]};
  width: 100%;
`;

const StyledDisplayOnHoursBar = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  z-index: 1;
  width: 100%;
`;

const StyledFlexTripsAndTransactions = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
`;

const StyledHoursBarDetails = styled.div`
  display: flex;
  flex: 1;
  position: relative;
  height: 100%;
  min-height: ${minutesSize * 60 * 24}rem;
`;

const StyledHour = styled.div`
  display: flex;
  align-items: center;
  min-height: ${minutesSize * 60}rem;
  max-height: ${minutesSize * 60}rem;
`;

const StyledHourLabel = styled.span`
  min-width: ${hoursColumnWidth}rem;
  text-align: center;
`;

const StyledHourLine = styled.div`
  flex: 1;
  height: 0.0625rem;
  margin-left: -0.5rem;
  background-color: ${colors["colors/borders/cells/cells"]};
`;

const StyledVerticalBar = styled.div<{ $hours: number }>`
  position: absolute;
  left: ${hoursColumnWidth}rem;
  height: ${({ $hours }) => $hours * minutesSize * 60}rem;
  width: 0.0625rem;
  background-color: ${colors["colors/borders/cells/cells"]};
`;

const StyledPeriod = styled.div<{ $height: number; $color: string }>`
  min-height: ${({ $height: height }) => height}rem;
  border-radius: 0.5rem;
  background-color: ${({ $color }) => $color};
  display: flex;
  position: relative;
`;

const StyledHoursBarContainerOverflow = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  width: 100%;
`;

const StyledHoursBarContainer = styled.div`
  border-radius: 0.5rem;
  border: 0.0625rem solid ${colors["colors/borders/cells/cells"]};
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  height: 100%;
`;

export const StyledFlex = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;
`;

export const StyledFlexHorizontal = styled.div`
  display: flex;
  position: relative;
  width: 100%;
  & svg {
    width: 1.4rem;
    height: 1.0625rem;
  }
`;

const StyledAbsoluteTransaction = styled.div`
  position: absolute;
  pointer-events: none;
  z-index: 2;
`;
